<script setup>
import { computed, ref } from "vue";
import SelectInput from "@/components/Select/SelectInput.vue";
import Modal from "@/components/Modal.vue";
import { useMainStore } from "../stores/mainStore";
import { PRICING_TICKET_TYPES } from "../stores/pricingStore";

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    ages: {
        type: String,
        default: "",
    },
    dates: {
        type: String,
        default: "",
    },
    session: {
        type: Object,
        required: true,
    },
    routeName: {
        type: String,
    },
    routeParams: {
        type: Object,
    },
    extended: {
        type: Boolean,
        default: false,
    },
    simpleFlow: {
        type: Boolean,
        default: false,
    },
    image: {
        type: String,
        default: "",
    },
    tickets: {
        type: Number,
        default: 0,
    },
    isSellingTicketFeatureActive: {
        type: Boolean,
        default: false,
    },
});

const mainStore = useMainStore();

const showAdminConfirmDeleteActivitySpinner = ref(false);
const showAdminConfirmDeleteActivity = ref(false);

async function updateStatusForSession(status) {
    if (status !== "Delete") {
        const res = await mainStore.updateStatus(props.session?.activity_id, status);

        if (res.data.success) {
            await mainStore.getData();
            // To update data in advanced flow
            mainStore.reSetCourse = true;
        }
    } else {
        showAdminConfirmDeleteActivity.value = true;
    }
}

async function submitDelete() {
    showAdminConfirmDeleteActivitySpinner.value = true;
    const res = await mainStore.removeActivity(props.session?.activity_id);
    showAdminConfirmDeleteActivitySpinner.value = true;
    showAdminConfirmDeleteActivity.value = false;

    if (res.data.success) {
        await mainStore.getData();

        // Updating data on the page for advanced flow
        mainStore.reSetCourse = true;
        setTimeout(() => {
            mainStore.reSetCourse = false;
        }, 500);
    }
}

function getStatusText(status) {
    switch (status) {
    case "submit":
        return "In Review";
    case "publish":
        return "Published";
    case "draft":
        return "In Draft";
    case "delete":
        return "Delete";
    default:
        return "In Draft";
    }
}

const showEditStatus = mainStore.isAdmin;
// show delete status only for Sophia user
const { showDeleteStatus } = mainStore;

const getStatuses = computed(() => {
    const statuses = [
        "Published",
        "In Review",
    ];

    if (showDeleteStatus) {
        statuses.push("Delete");
    }
    return statuses;
});

function countDatesPluralised(numEvents) {
    if (!numEvents) { // falsey , not set , 0 ...
        return "error";
    }

    if (numEvents > 1) {
        return `${numEvents} event dates`;
    }

    return "1 event date";
}

const hasActivePrice = computed(() => props.isSellingTicketFeatureActive
        // eslint-disable-next-line no-mixed-operators
        && (props.session.free === "1"
            || (props.session.free === "0" && props.session.timetable_prices?.length > 0)
            // eslint-disable-next-line no-mixed-operators
        ) || !props.isSellingTicketFeatureActive);

// eslint-disable-next-line eqeqeq
const getIsFree = computed(() => props.session.free == 1 || props.session.is_free == 1);

const timeTablePricesIsActive = computed(() => !!props.session.timetable_prices
    && props.session.timetable_prices?.length > 0);

const getTimetableSinglePrices = computed(() => props.session.timetable_prices
    .filter((price) => (price.ticket_type === PRICING_TICKET_TYPES.SINGLE || price.ticket_type
            === PRICING_TICKET_TYPES.TASTER)
        && price.price !== 0));

const getHasSinglePrice = computed(() => {
    if (props.isSellingTicketFeatureActive) {
        return !timeTablePricesIsActive.value
    || getTimetableSinglePrices.value.length > 0;
    }
    return props.session.price !== "";
});

const getSinglePrice = computed(() => {
    if (props.isSellingTicketFeatureActive) {
        const minValue = Math.min(...getTimetableSinglePrices.value.map((price) => price.price));
        return minValue ? `From £${minValue}` : "";
    }
    return `£${props.session.price}`;
});

const getTimetableRecurringPrices = computed(() => props.session.timetable_prices
    .filter((price) => (price.ticket_type === PRICING_TICKET_TYPES.WEEKLY
        || price.ticket_type === PRICING_TICKET_TYPES.DAILY) && price.price !== 0));

const getHasRecurringPrice = computed(() => {
    if (props.isSellingTicketFeatureActive) {
        return getTimetableRecurringPrices.value.length > 0;
    }
    return props.session.price_per_term > 0;
});

const getRecurringPrice = computed(() => {
    if (props.isSellingTicketFeatureActive) {
        const minValue = Math.min(...getTimetableRecurringPrices.value.map((price) => price.price));
        return minValue ? `From £${minValue}` : "";
    }
    return `£${props.session.price_per_term}`;
});

</script>

<template>
    <div class="event-card-container d-flex flex-row justify-content-between mb-3" data-test="card-event">
        <div class="d-flex flex-row">
            <div class="mr-2 d-flex align-items-center position-relative">
                <img v-if="props.image !== ''" class="card-img" :src="props.image" alt="" data-test="card-event-image"/>
                <div v-else>Image</div>
                <div v-if="props.session?.activity?.status === 'submit'" class="in-review-icon"
                     data-test="card-event-icon">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6" cy="6" r="6" fill="#FF8C00"/>
                    </svg>
                </div>
                <div class="under-review-info-block">
                    Under review by Loopla
                </div>
            </div>
            <div v-if="!props.extended && !props.simpleFlow">
                <div class="title" v-html="props.title" data-test="card-event-title"></div>
            </div>
            <div v-if="props.extended && !props.simpleFlow">
                <h4 v-html="`${props.session.time_start} - ${props.session.time_finish} &bull; ${props.session?.activity?.title} (${props.ages})`"></h4>
                <section v-if="hasActivePrice">
                    <div class="ml-1 session-info" data-test="card-event-info" v-if="getIsFree">
                        Free
                    </div>
                    <div class="ml-1 session-info" data-test="card-event-info" v-else>
                        {{ countDatesPluralised(props.session.eventCount) }} &bull;
                        <span v-if="getHasSinglePrice">
                            {{ getSinglePrice }} per session
                        </span>
                        <span v-if="getHasSinglePrice && getHasRecurringPrice">
                            /
                        </span>
                        <span v-if="getHasRecurringPrice">
                            {{ getRecurringPrice }} per course
                        </span>
                    </div>
                </section>
                <section v-else>
                    <div class="ml-1">
                        No price described
                    </div>
                </section>
            </div>
            <div v-if="props.simpleFlow">
                <div class="d-flex flex-row">
                    <div class="title" v-html="`${props.title} (${props.ages})`"></div>
                </div>
                <div class="d-flex flex-row mt-2">
                    {{ props.session.time_start }}
                    - {{ props.session.time_finish }}
                    &bull; {{ props.dates }} &bull;
                    <section v-if="hasActivePrice">
                        <div class="ml-1 session-info" data-test="card-event-info" v-if="getIsFree">
                            Free
                        </div>
                        <div class="ml-1 session-info" data-test="card-event-info" v-else>
                            <span v-if="getHasSinglePrice">
                                {{ getSinglePrice }} per session
                            </span>
                            <span v-if="getHasSinglePrice && getHasRecurringPrice">
                                /
                            </span>
                            <span v-if="getHasRecurringPrice">
                                {{ getRecurringPrice }} per course
                            </span>
                        </div>
                    </section>
                    <section v-else>
                        <div class="ml-1">
                            No price described
                        </div>
                    </section>
                </div>
            </div>
        </div>
        <div v-if="showEditStatus" class="select-container ml-auto">
            <SelectInput
                :options="getStatuses"
                :default="getStatusText(props.session?.activity?.status)"
                @change="updateStatusForSession"
                placeholder="Choose an event type"
            />
        </div>
        <div
            v-if="props.routeName && props.routeParams"
            class="ml-2 view-link-container"
            :class="{
                'ml-2': showEditStatus,
                'ml-auto': !showEditStatus
            }"
        >
            <div class="ml-3 d-flex flex-row justify-content-center view-link">
                <router-link
                    v-if="props.routeParams.termId !== ''"
                    :to="{
                        name: props.routeName,
                        params: props.routeParams,
                    }"
                    data-test="card-event-link-view"
                >
                    View
                </router-link>
            </div>
        </div>

        <Modal
            :show-modal="showAdminConfirmDeleteActivity"
            modal-dialog-class="modal-md"
            :title="'<h5>Delete event(s)</h5>'"
            :show-spinner="showAdminConfirmDeleteActivitySpinner"
            close-text="Cancel"
            submit-text="Yes-delete"
            close-btn-class="no-underline regular-cancel"
            submit-btn-class="warning"
            @submit-modal="submitDelete"
            @close-modal="showAdminConfirmDeleteActivity = false"
        >
            <div class="mt-4">
                Are you sure you want to delete event(s)?
            </div>
        </Modal>
    </div>
</template>

<style scoped>
h4 {
    font-size: 14px;
    font-weight: 600;
    line-height: 21px;
}

.session-info {
    font-size: 14px;
    line-height: 21px;
    color: #636066;
}

.event-card-container {
    height: 82px;
    left: 370px;
    top: 246px;
    background: #FFFFFF;
    box-shadow: 0px 0px 8px rgb(0 0 0 / 15%);
    border-radius: 15px;
    align-items: center;
    padding: 16px;
}

.event-card-container .title {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    color: #272529;
}

.select-container {
    min-width: 140px;
    margin-left: auto;
}

.actions-block {
    min-width: 176px;
}

.in-review-icon {
    position: absolute;
    top: -10px;
    right: -6px;
}

.in-review-icon:hover + .under-review-info-block {
    display: block;
    visibility: visible;
    opacity: 1;
}

.under-review-info-block {
    display: none;
    visibility: hidden;
    opacity: 0;
    position: absolute;
    color: #ffffff;
    top: 5px;
    right: 0;
    padding: 12px;
    width: 190px;
    height: 45px;
    background: #272529;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
}

.tag-icon {
    transform: rotate(90deg);
}

.available-tickets {
    display: inline-flex;
    padding: 8px 12px;
    justify-content: center;
    align-items: center;
    margin-left: 0.5rem;
    border-radius: 50px;
    background-color: #F7F7F7;
    font-weight: 600;
}

@media only screen and (max-width: 768px) {
    .event-card-container {
        height: auto;
        flex-wrap: wrap;
    }

    .view-link-container {
        margin-left: 0 !important;
        width: 100%;
    }

    .view-link {
        margin-top: 20px;
        margin-left: 0 !important;
        text-align: center;
        width: 100%;
    }
}
</style>
